import { createTheme, PaletteColor } from "@mui/material/styles";
import type {} from "@mui/x-date-pickers/themeAugmentation";
import { HHNTheme } from "hhn-commons";
import { ClickToOpenPickerTextField } from "./common/ClickToOpenPickerTextField";

declare module "@mui/material/styles" {
  interface Palette {
    primaryContrastText: PaletteColor["contrastText"];
  }

  interface PaletteOptions {
    primaryContrastText?: PaletteOptions["primary"];
  }
}

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 1280,
      lg: 1600,
      xl: 1920,
    },
  },
  palette: {
    primary: {
      light: "#5aadcb",
      main: HHNTheme.primary,
      dark: "#1b7693",
      contrastText: HHNTheme.common.white,
    },
    secondary: {
      light: "#8799a3",
      main: HHNTheme.secondary,
      dark: "#465258",
      contrastText: HHNTheme.common.white,
    },
    background: {
      default: HHNTheme.background,
      paper: "#f0f2f5",
    },
    success: {
      main: HHNTheme.success,
    },
    warning: {
      main: HHNTheme.warning,
    },
  },
  components: {
    // Open on field click; typing in the field still works.
    MuiDatePicker: { defaultProps: { keepOpenDuringFieldFocus: true } },
    MuiTimePicker: { defaultProps: { keepOpenDuringFieldFocus: true } },
    MuiDateTimePicker: { defaultProps: { keepOpenDuringFieldFocus: true } },
    MuiDateField: {
      defaultProps: { slots: { textField: ClickToOpenPickerTextField } },
    },
    MuiTimeField: {
      defaultProps: { slots: { textField: ClickToOpenPickerTextField } },
    },
    MuiDateTimeField: {
      defaultProps: { slots: { textField: ClickToOpenPickerTextField } },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          overflow: "hidden",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: HHNTheme.background, // white background
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: HHNTheme.background, // white background
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: HHNTheme.background, // white background
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: HHNTheme.background, // white background
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: HHNTheme.primary, // Replace with your desired color or theme variable
        },
      },
    },
  },
  typography: {
    htmlFontSize: 16,
    fontFamily: [
      "Roboto",
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Ubuntu",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
  },
  messagePanelBackground: "#f0f2f5",
  document: HHNTheme.document,
  message: HHNTheme.message,
  skeletonBackground: HHNTheme.skeletonBackground,
  tabsBackground: HHNTheme.tabsBackground,
  notesBackground: HHNTheme.notesBackground,
  lupa: HHNTheme.lupa,
  background: HHNTheme.background,
  deadSpaceBackground: "#E3E6E6",
});

declare module "@mui/material/styles" {
  interface Theme {
    document?: any;
    message?: {
      messageBackground?: string;
      visibleToPatient?: string;
      visibleToPhysician?: string;
      visibleToPatientAndPhysician?: string;
      deleted?: string;
      reminder?: string;
      infoBubble?: string;
      replyBubbleMediaBackground?: string;
    };
    messagePanelBackground: string;
    skeletonBackground?: string;
    tabsBackground: string;
    notesBackground: string;
    background: string;
    deadSpaceBackground: string;
    lupa: {
      met: string;
      partiallyMet: string;
      notMet: string;
    };
  }

  // allow configuration using `createTheme`
  interface ThemeOptions {
    document?: any;
    message?: {
      messageBackground?: string;
      visibleToPatient?: string;
      visibleToPhysician?: string;
      visibleToPatientAndPhysician?: string;
      deleted?: string;
      reminder?: string;
      infoBubble?: string;
    };
    messagePanelBackground: string;
    skeletonBackground?: string;
    tabsBackground: string;
    notesBackground: string;
    background: string;
    deadSpaceBackground: string;
    lupa: {
      met: string;
      partiallyMet: string;
      notMet: string;
    };
  }
}

export default theme;
